/**
 * Thank You Page
 *
 * Order confirmation page shown after successful checkout (or after
 * post-purchase upsell/downsell flow completes).
 * Mounts FunnelExperience with pageType='thank_you' for cross-sell offers.
 */

'use client';

import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FunnelExperience, FunnelErrorBoundary } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import { useIntl } from '@/lib/intl';
import { getOrderService } from '@/infrastructure/services/order.service';
import { formatCurrency } from '@/components/orders/utils';
import type { Order } from '@/domain/entities/order.entity';

const PageContainer = styled.main`
  max-width: 720px;
  margin: 0 auto;
  padding: 60px 24px;
  text-align: center;
`;

const CheckIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background: #22c55e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 32px;
`;

const OrderBox = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 12px;
  margin-bottom: 32px;
  text-align: left;
`;

const OrderLabel = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 4px;
`;

const OrderNumber = styled.p`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const SummaryBox = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 12px;
  margin-top: 24px;
  text-align: left;
`;

const SummaryTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const ItemName = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 4px;
`;

const ItemMeta = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.text.secondary}22;
`;

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TotalValue = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ContinueLink = styled.a`
  display: inline-block;
  margin-top: 24px;
  padding: 14px 32px;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

export function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const [fallbackOrderId] = useState(() => `ORD-${Date.now().toString(36).toUpperCase()}`);
  const orderId = searchParams.get('order_id') ?? searchParams.get('orderId') ?? fallbackOrderId;
  const [order, setOrder] = useState<Order | null>(null);
  const urlParameters = Object.fromEntries(searchParams.entries());
  const handleFunnelAddToCart = useFunnelAddToCart();
  const { countryConfig } = useIntl();
  const currencyCode = countryConfig.currency?.code ?? 'USD';
  const locale = countryConfig.locale ?? 'en-US';
  const currencySymbol = countryConfig.currency?.format ?? '$';

  useEffect(() => {
    let cancelled = false;
    if (!orderId) return;
    getOrderService()
      .getOrderById(orderId)
      .then((data) => {
        if (!cancelled) setOrder(data);
      })
      .catch(() => {
        if (!cancelled) setOrder(null);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const totalDisplay = order
    ? formatCurrency(order.totals.total.display, locale, currencyCode, currencySymbol)
    : null;

  return (
    <PageContainer>
      <CheckIcon>✓</CheckIcon>
      <Title>Thank You for Your Order!</Title>
      <Subtitle>Your order has been placed successfully.</Subtitle>

      <OrderBox>
        <OrderLabel>Order Number</OrderLabel>
        <OrderNumber>{orderId}</OrderNumber>
      </OrderBox>

      <InfoText>
        We&apos;ll send you a confirmation email with your order details and tracking information
        once your order ships.
      </InfoText>

      {order ? (
        <SummaryBox>
          <SummaryTitle>Order Summary</SummaryTitle>
          <ItemList>
            {order.line_items.map((item) => (
              <ItemRow key={item.id}>
                <div>
                  <ItemName>{item.name}</ItemName>
                  <ItemMeta>Qty: {item.quantity}</ItemMeta>
                </div>
                <ItemMeta>
                  {formatCurrency(
                    item.unit_price.display * item.quantity,
                    locale,
                    currencyCode,
                    currencySymbol,
                  )}
                </ItemMeta>
              </ItemRow>
            ))}
          </ItemList>
          {totalDisplay ? (
            <TotalRow>
              <TotalLabel>Total</TotalLabel>
              <TotalValue>{totalDisplay}</TotalValue>
            </TotalRow>
          ) : null}
        </SummaryBox>
      ) : null}

      <ContinueLink href="/products">Continue Shopping</ContinueLink>

      <FunnelErrorBoundary>
        <FunnelExperience
          pageType="thank_you"
          context={{ url: '/thank-you', country: countryConfig.countryCode, orderId, urlParameters }}
          onAddToCart={handleFunnelAddToCart}
        />
      </FunnelErrorBoundary>
    </PageContainer>
  );
}
